import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { DataGrid } from '@mui/x-data-grid';
import ApiService from '../../../services/ApiService';
import {
  Autocomplete,
  Button,
  FormControl,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'oidc-react';

const DataTable = forwardRef(
  (
    {
      taskListAuthorization,
      stateRegion,
      stateLokasi,
      dataRole,
      stateKegiatan,
      download,
      onReAssignCreator,
      onUnauthorized,
    },
    ref
  ) => {
    const { userData } = useAuth();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [regionId, setRegionId] = useState(0);
    const [dataRegion, setDataRegion] = stateRegion;
    const [dataLokasi, setDataLokasi] = stateLokasi;
    const [dataKegiatan, setDataKegiatan] = stateKegiatan;
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [isLoadingActionMenu, setIsLoadingActionMenu] = useState(false);

    const history = useNavigate();

    const [pagination, setPagination] = useState({
      totalCount: 0,
      pageSize: 5,
      currentPage: 1,
      totalPages: 0,
      previousPage: false,
      nextPage: false,
      payload: {
        regionId: 0,
        locationId: 0,
        kegiatan: 0,
        role: taskListAuthorization.aC_EXPORT_DATA ? '' : 'creator',
        emailLogin: '',
        textSearch: '',
      },
    });

    const fetchData = async () => {
      setIsLoading(true);
      ApiService.getActionTrackingTaskList({
        page: pagination.currentPage,
        itemPerPage: pagination.pageSize,
        regionId: pagination.payload.regionId,
        locationId: pagination.payload.locationId,
        kegiatan: pagination.payload.kegiatan,
        role: pagination.payload.role,
        emailLogin: userData?.profile?.email,
        textSearch: pagination.payload.textSearch,
      })
        .then(({ data }) => {
          setData(data.payload.data);
          setPagination({ ...pagination, ...data.payload.pagination });
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
          setData([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    useEffect(() => {
      fetchData();
    }, [
      pagination.currentPage,
      pagination.pageSize,
      pagination.payload.regionId,
      pagination.payload.locationId,
      pagination.payload.kegiatan,
      pagination.payload.role,
      pagination.payload.textSearch,
    ]);

    const handlePageChange = (page) => {
      setPagination((prevPagination) => ({
        ...prevPagination,
        currentPage: page + 1,
      }));
    };

    const onChangeRegion = (event, value) => {
      if (!value) {
        setRegionId(0);
        setDataLokasi([]);
        setPagination((prevPagination) => ({
          ...prevPagination,
          payload: {
            ...prevPagination.payload,
            regionId: 0,
          },
        }));
        return;
      }
      setPagination((prevPagination) => ({
        ...prevPagination,
        payload: {
          ...prevPagination.payload,
          regionId: value.id,
        },
      }));
      setRegionId(value.id);
    };

    const getDataLokasi = () => {
      if (!regionId) return;
      setIsLoading(true);
      ApiService.getMsLokasi(regionId)
        .then((res) => {
          var result = res.data.payload;
          setDataLokasi(result);
        })
        .catch(() => {
          setDataLokasi([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    useEffect(() => {
      getDataLokasi();
    }, [regionId]);

    const handleButtonClick = (event, params) => {
      setAnchorEl(event.currentTarget);
      setSelectedRowId(params.row.paramActionTrackingEncode);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setSelectedRowId(null);
    };

    const handleMenuItemClick = (event, action, row) => {
      if (action === 'RE-ASSIGN') {
        onReAssignCreator(row);
        handleMenuClose();
        return;
      }

      setIsLoadingActionMenu(true);
      ApiService.getActionTrackingById(row.paramActionTrackingEncode)
        .then(({ data }) => {
          if (data && !data.isOk) {
            onUnauthorized(data.message);
            return;
          }
          if (
            data &&
            data.payload.statusHeaderName !== 'Completed' &&
            (data.payload.role === 'Admin' ||
              data.payload.role === 'Creator') &&
            (data.payload.statusHeaderName === 'Draft' || 'Open')
          ) {
            history(`/hsse/action-tracking/${selectedRowId}`, {
              state: { isEdit: true },
            });
          } else {
            history(`/hsse/action-tracking/${selectedRowId}`);
          }
        })
        .finally(() => {
          setIsLoadingActionMenu(false);
        });

      handleMenuClose();
    };

    const updateRow = (row) => {
      fetchData();
    };

    const getMenuItems = (row) => {
      const availableActions = ['VIEW'];

      if (taskListAuthorization.aC_EXPORT_DATA && row.statusName !== 'Closed') {
        availableActions.push('RE-ASSIGN');
      }

      return availableActions.map((action) => (
        <MenuItem
          key={action}
          onClick={(event) => handleMenuItemClick(event, action, row)}
        >
          {action}
        </MenuItem>
      ));
    };

    const getPaginationPayload = () => {
      return pagination.payload;
    };

    useImperativeHandle(ref, () => ({
      getPaginationPayload,
      updateRow,
    }));

    const handleSearch = (event) => {
      if (event.key === 'Enter' && event.target.value.length === 0) {
        setPagination({
          ...pagination,
          payload: {
            ...pagination.payload,
            textSearch: '',
          },
        });
      }
      if (event.key === 'Enter' && event.target.value.length >= 3) {
        setPagination({
          ...pagination,
          payload: {
            ...pagination.payload,
            textSearch: event.target.value,
          },
        });
      }
    };

    return (
      <Grid item xs={12}>
        <Grid
          container
          spacing={2}
          marginBottom={3}
          justifyContent="space-around"
          alignItems="center"
        >
          <Grid
            item
            xs={10}
            container
            justifyContent="space-between"
            alignItems="center"
            spacing={2}
          >
            <Grid item xs={3}>
              <FormControl fullWidth>
                <Autocomplete
                  name="idRegional"
                  onChange={onChangeRegion}
                  required
                  getOptionLabel={(option) => option.name}
                  options={dataRegion}
                  renderInput={(params) => (
                    <TextField {...params} label="Regional" />
                  )}
                  getOptionSelected={(option, value) =>
                    option.name === value.name
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <Autocomplete
                  name="idLokasi"
                  onChange={(e, value) => {
                    setPagination({
                      ...pagination,
                      payload: {
                        ...pagination.payload,
                        locationId: value ? value.id : 0,
                      },
                    });
                  }}
                  required
                  getOptionLabel={(option) => option.name}
                  options={dataLokasi}
                  renderInput={(params) => (
                    <TextField {...params} label="Lokasi" />
                  )}
                  getOptionSelected={(option, value) =>
                    option.name === value.name
                  }
                  loading={isLoading}
                />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <Autocomplete
                  name="kegiatan"
                  onChange={(e, value) => {
                    setPagination({
                      ...pagination,
                      payload: {
                        ...pagination.payload,
                        kegiatan: value ? value.id : 0,
                      },
                    });
                  }}
                  required
                  getOptionLabel={(option) => option.name}
                  options={dataKegiatan}
                  renderInput={(params) => (
                    <TextField {...params} label="Kegiatan" />
                  )}
                  getOptionSelected={(option, value) =>
                    option.name === value.name
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl fullWidth>
                <Autocomplete
                  name="role"
                  onChange={(e, value) => {
                    setPagination({
                      ...pagination,
                      payload: {
                        ...pagination.payload,
                        role: value
                          ? value.name.toLowerCase()
                          : taskListAuthorization.aC_EXPORT_DATA
                          ? ''
                          : 'creator',
                      },
                    });
                  }}
                  required
                  getOptionLabel={(option) => option.name}
                  options={
                    taskListAuthorization.aC_EXPORT_DATA
                      ? dataRole
                      : dataRole.filter((role) => role.name !== 'All')
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Role" />
                  )}
                  getOptionSelected={(option, value) =>
                    option.name === value.name
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid
            item
            xs={2}
            justifyContent="center"
            alignItems="center"
            container
          >
            <FormControl fullWidth>
              <TextField
                label="Search"
                variant="outlined"
                onKeyDown={handleSearch}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} marginBottom={3}>
          <Grid item xs={12}>
            <DataGrid
              autoHeight={true}
              sx={{ width: '100%' }}
              rows={data}
              disableColumnFilter={true}
              disableColumnSelector={true}
              disableSelectionOnClick={true}
              disableColumnMenu={true}
              columns={[
                {
                  field: 'action',
                  headerName: 'Action',
                  sortable: false,
                  renderCell: (params) => (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={(event) => handleButtonClick(event, params)}
                        disabled={isLoadingActionMenu}
                      >
                        Action
                      </Button>
                      <Menu
                        anchorEl={anchorEl}
                        open={
                          Boolean(anchorEl) &&
                          selectedRowId === params.row.paramActionTrackingEncode
                        }
                        onClose={handleMenuClose}
                      >
                        {getMenuItems(params.row)}
                      </Menu>
                    </>
                  ),
                },
                {
                  field: 'statusName',
                  headerName: 'Status',
                  sortable: false,
                  renderCell: (params) => {
                    let cellValue = params.value;
                    let cellColor = '';

                    switch (cellValue) {
                      case 'Open':
                        cellColor = '#ff9100'; // Orange
                        break;
                      case 'On Progress':
                        cellColor = '#FFFF00'; // Light Blue
                        break;
                      case 'In Progress':
                        cellColor = '#ffeb3b'; // Light Blue
                        break;
                      case 'Closed':
                        cellColor = '#98FB98';
                        break;
                      case 'Completed':
                        cellColor = '#333333'; // Dark Gray
                        break;
                      default:
                        cellColor = '#000000'; // Black
                        break;
                    }

                    return (
                      <div
                        style={{
                          borderRadius: '5px',
                          border: '#adada8 2px solid',
                          padding: '8px 2px',
                          width: '120px',
                          alignItems: 'center',
                          textAlign: 'center',
                        }}
                      >
                        <span
                          style={{
                            color: cellColor,
                            fontWeight: 'bold',
                          }}
                        >
                          {cellValue}
                        </span>
                      </div>
                    );
                  },
                },
                {
                  field: 'nomorActionTracking',
                  headerName: 'No. Action Tracking',
                  width: 300,
                  sortable: false,
                },
                {
                  field: 'judul',
                  headerName: 'Judul',
                  width: 340,
                  sortable: false,
                  renderCell: (params) => (
                    <Tooltip title={params.value}>
                      <span>
                        {params.value && params.value.length > 25
                          ? `${params.value.substring(0, 40)}...`
                          : params.value}
                      </span>
                    </Tooltip>
                  ),
                },
                {
                  field: 'idKegiatan',
                  headerName: 'Kegiatan',
                  hide: true,
                },
                {
                  field: 'namaKegiatan',
                  headerName: 'Kegiatan',
                  width: 240,
                  sortable: false,
                },
                {
                  field: 'idRegional',
                  headerName: 'Regional ID',
                  hide: true,
                },
                {
                  field: 'namaRegional',
                  headerName: 'Regional',
                  sortable: false,
                  width: 240,
                },
                {
                  field: 'idLokasi',
                  headerName: 'Location ID',
                  hide: true,
                },
                {
                  field: 'namaLokasi',
                  headerName: 'Location',
                  sortable: false,
                  width: 240,
                },
                {
                  field: 'idStatus',
                  headerName: 'Status ID',
                  hide: true,
                },

                {
                  field: 'creatorEmail',
                  headerName: 'Creator',
                  sortable: false,
                  width: 240,
                },
                {
                  field: 'idStatus',
                  headerName: 'Status ID',
                  hide: true,
                },
                {
                  field: 'paramActionTrackingEncode',
                  hide: true,
                  headerName: 'Encoded',
                },
                ,
              ]}
              pagination
              rowCount={pagination.totalCount}
              page={pagination.currentPage - 1}
              onPageChange={handlePageChange}
              paginationMode="server"
              getRowId={(row) => row.paramActionTrackingEncode}
              pageSize={pagination.pageSize}
              onPageSizeChange={(newPageSize) =>
                setPagination({
                  ...pagination,
                  pageSize: newPageSize,
                  currentPage: 1,
                })
              }
              rowsPerPageOptions={[1, 5, 10, 20, 50]} // Customize available options
              loading={isLoading}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

export default DataTable;
